import endSearchSession from '../actions/endSearchSession';
import initializeSearchSessionIds from '../actions/initializeSearchSessionIds';
import onSearchSessionEnded from '../actions/onSearchSessionEnded';
import onSearchSessionStarted from '../actions/onSearchSessionStarted';
import resetSearchStore from '../actions/resetSearchStore';
import startSearchSession from '../actions/startSearchSession';
import isSearchSessionActive from '../selectors/isSearchSessionActive';
import getScenarioStore from '../store/getScenarioStore';
import getSubstrateSearchScenarioBySearchScenarioId from '../utils/getSubstrateSearchScenarioBySearchScenarioId';
import { logUsage } from 'owa-analytics';
import { SearchScenarioId } from 'owa-search-next-common';
import { lazySubstrateSearchInitOperation } from 'owa-search-service';
import { orchestrator } from 'satcheljs';
import {
    lazyClearLocalContentInstrumentationCache,
    lazyFlushNonRenderedItemsFromCache,
    lazyLogSessionEnd,
} from 'owa-search-service';

orchestrator(startSearchSession, actionMessage => {
    const { scenarioId, actionSource, shouldStartSearch } = actionMessage;

    // If session is already active, then return.
    if (isSearchSessionActive(scenarioId)) {
        return;
    }

    initializeSearchSessionIds(scenarioId);
    const store = getScenarioStore(scenarioId);

    // Make init request if search box is 3S-powered.
    // TODO: Evaluate if this should be here (or in caller's code).
    if (store.isUsing3S) {
        lazySubstrateSearchInitOperation.import().then(substrateSearchInitOperation => {
            substrateSearchInitOperation(
                store.nextSearchQueryId,
                getSubstrateSearchScenarioBySearchScenarioId(scenarioId),
                null /* X-Client-Flights header value */
            );
        });
    }

    // Dispatch action to notify caller that common work is done.
    onSearchSessionStarted(actionSource, shouldStartSearch, scenarioId);

    // Log usage datapoint.
    logUsage('Search_SessionStarted', [SearchScenarioId[scenarioId], actionSource]);
});

orchestrator(endSearchSession, actionMessage => {
    const { scenarioId, actionSource = 'Unknown' } = actionMessage;
    const store = getScenarioStore(scenarioId);

    const searchSessionGuid = store.searchSessionGuid;
    const isLastRequestQF = store.latestTraceId === store.latestQFTraceId;
    const latestTraceId = store.latestTraceId;
    const isUsing3S = store.isUsing3S;

    // Throw if user isn't already in a search session.
    if (!isSearchSessionActive(scenarioId)) {
        throw new Error('Search session should not be ended if not active.');
    }

    // Dispatch action to reset store (after getting values from it).
    resetSearchStore(scenarioId);

    /**
     * If using 3S, log instrumentation to end session, and flush any
     * non-rendered events from the event cache.
     */
    if (isUsing3S) {
        const substrateSearchScenario = getSubstrateSearchScenarioBySearchScenarioId(scenarioId);

        lazyLogSessionEnd.importAndExecute(
            isLastRequestQF ? store.latestRenderedQFTraceId : latestTraceId,
            new Date(),
            substrateSearchScenario
        );

        // Flush 3S telemetry events from cache.
        lazyFlushNonRenderedItemsFromCache.importAndExecute(substrateSearchScenario);
        lazyClearLocalContentInstrumentationCache.importAndExecute();
    }

    /**
     * After common "end session" work is done, dispatch event to notify
     * consumer that search session has ended.
     */
    onSearchSessionEnded(searchSessionGuid, scenarioId, latestTraceId, actionSource);

    logUsage('SearchSessionEnded', [actionSource, scenarioId, searchSessionGuid]);
});
